// src/services/firestoreService.js
import { db, functions } from "../firebase-config";
import { httpsCallable } from "firebase/functions";
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  orderBy, 
  serverTimestamp,
  where
} from "firebase/firestore";

// Helper to map Firestore documents to include both id and _id (compatibility with existing frontend)
const mapDoc = (docSnap) => {
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  const formatted = { 
    id: docSnap.id, 
    _id: docSnap.id, 
    ...data 
  };
  
  // Format timestamps for frontend Compatibility
  if (data.createdAt && typeof data.createdAt.toDate === "function") {
    formatted.createdAt = data.createdAt.toDate().toISOString();
  }
  if (data.deadline && typeof data.deadline.toDate === "function") {
    formatted.deadline = data.deadline.toDate().toISOString();
  }
  if (data.expiredAt && typeof data.expiredAt.toDate === "function") {
    formatted.expiredAt = data.expiredAt.toDate().toISOString();
  }
  return formatted;
};

// Helper to map list of documents
const mapDocs = (querySnapshot) => {
  return querySnapshot.docs.map(mapDoc);
};

// Helper for generating unique client-side IDs for sub-documents (like images in a gallery folder)
const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// ==================== COURSES ====================
export const getCourses = async () => {
  const colRef = collection(db, "courses");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const getCourse = async (id) => {
  const docRef = doc(db, "courses", id);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const addCourse = async (courseData) => {
  const colRef = collection(db, "courses");
  const docRef = await addDoc(colRef, {
    ...courseData,
    createdAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const updateCourse = async (id, courseData) => {
  const docRef = doc(db, "courses", id);
  await updateDoc(docRef, courseData);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const deleteCourse = async (id) => {
  const docRef = doc(db, "courses", id);
  await deleteDoc(docRef);
  return { success: true };
};

// ==================== SUCCESS STORIES ====================
export const getSuccessStories = async () => {
  const colRef = collection(db, "successStories");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const addSuccessStory = async (storyData) => {
  const colRef = collection(db, "successStories");
  const docRef = await addDoc(colRef, {
    ...storyData,
    createdAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const deleteSuccessStory = async (id) => {
  const docRef = doc(db, "successStories", id);
  await deleteDoc(docRef);
  return { success: true };
};

// ==================== FAQS ====================
export const getFaqs = async () => {
  const colRef = collection(db, "faqs");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const addFaq = async (faqData) => {
  const colRef = collection(db, "faqs");
  const docRef = await addDoc(colRef, {
    ...faqData,
    createdAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const updateFaq = async (id, faqData) => {
  const docRef = doc(db, "faqs", id);
  await updateDoc(docRef, faqData);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const deleteFaq = async (id) => {
  const docRef = doc(db, "faqs", id);
  await deleteDoc(docRef);
  return { success: true };
};

// ==================== WHY CHOOSE ====================
export const getWhyChooseItems = async () => {
  const colRef = collection(db, "whyChoose");
  const snapshot = await getDocs(colRef);
  const items = mapDocs(snapshot);
  return items.sort((a, b) => {
    const orderA = typeof a.order === "number" && !isNaN(a.order) ? a.order : (parseInt(a.order, 10) || 0);
    const orderB = typeof b.order === "number" && !isNaN(b.order) ? b.order : (parseInt(b.order, 10) || 0);
    if (orderA !== orderB) return orderA - orderB;
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });
};

export const addWhyChooseItem = async (itemData) => {
  const colRef = collection(db, "whyChoose");
  const numericOrder = typeof itemData.order === "number" && !isNaN(itemData.order) 
    ? itemData.order 
    : (parseInt(itemData.order, 10) || 0);
  const docRef = await addDoc(colRef, {
    title: itemData.title || "",
    description: itemData.description || "",
    imageUrl: itemData.imageUrl || "",
    order: numericOrder,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  const mapped = mapDoc(snapshot);
  if (mapped && !mapped.createdAt) {
    mapped.createdAt = new Date().toISOString();
  }
  return mapped;
};

export const updateWhyChooseItem = async (id, itemData) => {
  const docRef = doc(db, "whyChoose", id);
  const numericOrder = typeof itemData.order === "number" && !isNaN(itemData.order) 
    ? itemData.order 
    : (parseInt(itemData.order, 10) || 0);
  const updatePayload = {
    title: itemData.title,
    description: itemData.description,
    order: numericOrder,
    updatedAt: serverTimestamp()
  };
  if (itemData.imageUrl !== undefined) {
    updatePayload.imageUrl = itemData.imageUrl;
  }
  await updateDoc(docRef, updatePayload);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const deleteWhyChooseItem = async (id) => {
  const docRef = doc(db, "whyChoose", id);
  await deleteDoc(docRef);
  return { success: true };
};

// ==================== STUDENT ENQUIRIES ====================
export const submitStudentEnquiry = async (studentData) => {
  const colRef = collection(db, "students");
  const docRef = await addDoc(colRef, {
    ...studentData,
    createdAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const getStudentEnquiries = async () => {
  const colRef = collection(db, "students");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const deleteStudentEnquiry = async (id) => {
  const docRef = doc(db, "students", id);
  await deleteDoc(docRef);
  return { success: true };
};

// ==================== GALLERY FOLDERS ====================
export const getGalleryFolders = async () => {
  const colRef = collection(db, "galleryFolders");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const createGalleryFolder = async (name) => {
  const colRef = collection(db, "galleryFolders");
  const docRef = await addDoc(colRef, {
    name,
    images: [],
    createdAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const deleteGalleryFolder = async (id) => {
  const docRef = doc(db, "galleryFolders", id);
  await deleteDoc(docRef);
  return { success: true };
};

export const addImageToFolder = async (folderId, imageUrl, caption) => {
  const docRef = doc(db, "galleryFolders", folderId);
  const folderSnap = await getDoc(docRef);
  if (!folderSnap.exists()) throw new Error("Folder not found");
  
  const currentFolder = folderSnap.data();
  const currentImages = currentFolder.images || [];
  
  const newImageId = generateUniqueId();
  const newImage = {
    _id: newImageId,
    id: newImageId,
    url: imageUrl,
    caption: caption || "",
    createdAt: new Date().toISOString()
  };
  
  await updateDoc(docRef, {
    images: [...currentImages, newImage]
  });
  
  const updatedSnap = await getDoc(docRef);
  return mapDoc(updatedSnap);
};

export const deleteImageFromFolder = async (folderId, imageId) => {
  const docRef = doc(db, "galleryFolders", folderId);
  const folderSnap = await getDoc(docRef);
  if (!folderSnap.exists()) throw new Error("Folder not found");
  
  const currentFolder = folderSnap.data();
  const currentImages = currentFolder.images || [];
  
  // Filter out the image by either _id or id
  const updatedImages = currentImages.filter(img => img._id !== imageId && img.id !== imageId);
  
  await updateDoc(docRef, {
    images: updatedImages
  });
  
  const updatedSnap = await getDoc(docRef);
  return mapDoc(updatedSnap);
};

// ==================== STUDENT PORTAL HELPERS ====================
export const verifyStudentEmailExists = async (email) => {
  const colRef = collection(db, "studentProfiles");
  const q = query(colRef, where("email", "==", email.toLowerCase().trim()));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

export const getStudentEnquiriesByEmail = async (email) => {
  const colRef = collection(db, "students");
  const q = query(colRef, where("email", "==", email.toLowerCase().trim()));
  const snapshot = await getDocs(q);
  // Sort client-side to avoid requiring composite indexes
  const items = mapDocs(snapshot);
  return items.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
};

export const getInstructors = async () => {
  const colRef = collection(db, "instructors");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

// ==================== INSTRUCTOR PORTAL HELPERS ====================
export const verifyInstructorEmailExists = async (email) => {
  const colRef = collection(db, "instructors");
  const q = query(colRef, where("email", "==", email.toLowerCase().trim()));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

export const getInstructorProfile = async (uid) => {
  const docRef = doc(db, "instructors", uid);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const updateInstructorProfile = async (uid, data) => {
  const docRef = doc(db, "instructors", uid);
  await updateDoc(docRef, data);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const getInstructorBatches = async (instructorUid) => {
  const colRef = collection(db, "batches");
  const q = query(colRef, where("createdByInstructorId", "==", instructorUid));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const getBatchStudents = async (studentUids) => {
  if (!studentUids || studentUids.length === 0) return [];
  const promises = studentUids.map(uid => getDoc(doc(db, "studentProfiles", uid)));
  const snapshots = await Promise.all(promises);
  return snapshots.map(mapDoc).filter(Boolean);
};

export const searchRegisteredStudents = async (queryStr) => {
  const colRef = collection(db, "studentProfiles");
  const snapshot = await getDocs(colRef);
  const students = mapDocs(snapshot);
  
  if (!queryStr || !queryStr.trim()) {
    return students;
  }
  
  const term = queryStr.toLowerCase().trim();
  return students.filter(s => 
    (s.regNo && s.regNo.toLowerCase().includes(term)) ||
    (s.displayName && s.displayName.toLowerCase().includes(term)) ||
    (s.email && s.email.toLowerCase().includes(term))
  );
};

// ==================== STREAK & COIN ENGINE ====================

/**
 * Calls the recordDailyLogin Cloud Function to handle streak logic and coin award.
 * Returns: { alreadyLoggedIn, profile, streakBroken, missedDays, previousStreak, freezeUsed, freezeAwarded }
 */
export const callRecordDailyLogin = async () => {
  const fn = httpsCallable(functions, "recordDailyLogin");
  const result = await fn();
  return result.data;
};

/**
 * Calls the recoverStreak Cloud Function to spend coins and restore a broken streak.
 * @param {number} missedDays - Number of days missed
 * @param {number} previousStreak - The streak value before it was broken
 * Returns: { success, newCoins, restoredStreak }
 */
export const callRecoverStreak = async (missedDays, previousStreak) => {
  const fn = httpsCallable(functions, "recoverStreak");
  const result = await fn({ missedDays, previousStreak });
  return result.data;
};

/**
 * Calls the startFreshStreak Cloud Function to reset streak to 1 without coin deduction.
 * Returns: { success, streakCurrent }
 */
export const callStartFreshStreak = async () => {
  const fn = httpsCallable(functions, "startFreshStreak");
  const result = await fn();
  return result.data;
};

/**
 * Fetches the coin transaction history for a student.
 * @param {string} userId
 * Returns: Array of transaction objects sorted by timestamp descending.
 */
export const getCoinTransactions = async (userId) => {
  const colRef = collection(db, "studentProfiles", userId, "coinTransactions");
  const q = query(colRef, orderBy("timestamp", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

/**
 * Fetches login history entries for the streak calendar display.
 * @param {string} userId
 * Returns: Array of { date, timestamp } objects.
 */
export const getLoginHistory = async (userId) => {
  const colRef = collection(db, "studentProfiles", userId, "loginHistory");
  const q = query(colRef, orderBy("date", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

// ==================== COURSE PROGRESS ====================
export const getStudentCourseProgress = async (studentUid) => {
  const colRef = collection(db, "studentProfiles", studentUid, "courseProgress");
  const snapshot = await getDocs(colRef);
  return mapDocs(snapshot);
};

export const updateStudentCourseProgress = async (studentUid, courseId, progressData) => {
  const docRef = doc(db, "studentProfiles", studentUid, "courseProgress", courseId);
  await setDoc(docRef, {
    ...progressData,
    courseId,
    lastAccessed: serverTimestamp()
  }, { merge: true });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

// ==================== TEST SCORES ====================
export const getStudentTestScores = async (studentUid) => {
  const colRef = collection(db, "studentProfiles", studentUid, "testScores");
  const q = query(colRef, orderBy("timestamp", "asc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const addStudentTestScore = async (studentUid, scoreData) => {
  const fn = httpsCallable(functions, "addTestScore");
  const res = await fn({
    studentUid,
    testName: scoreData.testName,
    testDate: scoreData.testDate || new Date().toISOString().split("T")[0],
    marksObtained: scoreData.marksObtained,
    totalMarks: scoreData.totalMarks
  });
  if (res.data?.success) {
    const docRef = doc(db, "studentProfiles", studentUid, "testScores", res.data.scoreId);
    const snapshot = await getDoc(docRef);
    return mapDoc(snapshot);
  }
  throw new Error("Failed to add test score.");
};

export const deleteStudentTestScore = async (studentUid, scoreId) => {
  const fn = httpsCallable(functions, "deleteTestScore");
  const res = await fn({ studentUid, scoreId });
  return res.data;
};

// ==================== BATCH COURSE PROGRESS & STUDENT BATCHES ====================
export const updateBatchCourseProgress = async (batchId, courseId, progressData) => {
  const docRef = doc(db, "batches", batchId);
  await updateDoc(docRef, {
    courseId,
    courseTitle: progressData.courseTitle,
    completedChapters: progressData.completedChapters,
    completionPercentage: progressData.completionPercentage
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const getStudentBatches = async (studentUid) => {
  const colRef = collection(db, "batches");
  const q = query(colRef, where("studentUids", "array-contains", studentUid));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

// ==================== BATCH ASSIGNMENTS ====================
export const getBatchAssignments = async (batchId) => {
  const colRef = collection(db, "batches", batchId, "assignments");
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

export const addBatchAssignment = async (batchId, assignmentData) => {
  const colRef = collection(db, "batches", batchId, "assignments");
  const docRef = await addDoc(colRef, {
    ...assignmentData,
    createdAt: serverTimestamp()
  });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const getAssignmentSubmissions = async (batchId, assignmentId) => {
  const colRef = collection(db, "batches", batchId, "assignments", assignmentId, "submissions");
  const snapshot = await getDocs(colRef);
  return mapDocs(snapshot);
};

export const getStudentAssignmentSubmission = async (batchId, assignmentId, studentUid) => {
  const docRef = doc(db, "batches", batchId, "assignments", assignmentId, "submissions", studentUid);
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const updateBatchAssignment = async (batchId, assignmentId, updateData) => {
  const docRef = doc(db, "batches", batchId, "assignments", assignmentId);
  await updateDoc(docRef, { ...updateData, updatedAt: serverTimestamp() });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const deleteBatchAssignment = async (batchId, assignmentId) => {
  const docRef = doc(db, "batches", batchId, "assignments", assignmentId);
  await deleteDoc(docRef);
  return { success: true };
};

export const submitStudentAssignment = async (batchId, assignmentId, studentUid, submissionData) => {
  const docRef = doc(db, "batches", batchId, "assignments", assignmentId, "submissions", studentUid);
  await setDoc(docRef, {
    ...submissionData,
    studentUid,
    submittedAt: serverTimestamp()
  }, { merge: true });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

// ==================== ADMIN BATCH & ACTIVITY LOG SERVICES ====================
export const updateBatchStatus = async (batchId, isActive) => {
  const docRef = doc(db, "batches", batchId);
  await updateDoc(docRef, { active: isActive });
  const snapshot = await getDoc(docRef);
  return mapDoc(snapshot);
};

export const getAllBatches = async () => {
  const colRef = collection(db, "batches");
  const snapshot = await getDocs(colRef);
  return mapDocs(snapshot);
};

export const getInstructorActivityLogs = async () => {
  const colRef = collection(db, "instructorActivityLogs");
  const q = query(colRef, orderBy("timestamp", "desc"));
  const snapshot = await getDocs(q);
  return mapDocs(snapshot);
};

// ==================== DATE & TIMEZONE HELPERS ====================
/**
 * Formats current date as YYYY-MM-DD in Asia/Kolkata timezone (aligned with backend rules).
 */
export const getTodayDateString = () => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).format(new Date());
  } catch (e) {
    return new Date().toISOString().split("T")[0];
  }
};

/**
 * Calculates the difference in full calendar days between two YYYY-MM-DD date strings.
 */
export const calculateCalendarDaysDifference = (dateStr1, dateStr2) => {
  if (!dateStr1 || !dateStr2) return 0;
  const d1 = new Date(dateStr1 + "T00:00:00Z");
  const d2 = new Date(dateStr2 + "T00:00:00Z");
  const diffMs = d2.getTime() - d1.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

// ==================== HERO SECTION IMAGES ====================
/**
 * Fetches all additional hero section images from Firestore ordered by 'order' ascending.
 */
export const getHeroImages = async () => {
  try {
    const colRef = collection(db, "heroImages");
    const q = query(colRef, orderBy("order", "asc"));
    const snapshot = await getDocs(q);
    return mapDocs(snapshot);
  } catch (err) {
    console.warn("Direct Firestore getHeroImages failed (code:", err.code, ", message:", err.message, "). Falling back to Cloud Function...");
    try {
      const fn = httpsCallable(functions, "getHeroImages");
      const res = await fn();
      return res.data?.items || [];
    } catch (fnErr) {
      console.error("Cloud Function getHeroImages also failed (code:", fnErr.code, ", message:", fnErr.message, ")");
      return [];
    }
  }
};

/**
 * Adds an additional hero image document to Firestore.
 * Ensures 'order' is stored as a number.
 */
export const addHeroImage = async (data) => {
  const numericOrder = typeof data.order === "number" && !isNaN(data.order) 
    ? data.order 
    : (parseInt(data.order, 10) || 0);

  try {
    const colRef = collection(db, "heroImages");
    const docRef = await addDoc(colRef, {
      imageUrl: data.imageUrl,
      order: numericOrder,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    const snapshot = await getDoc(docRef);
    return mapDoc(snapshot);
  } catch (err) {
    console.warn("Direct Firestore addHeroImage failed (code:", err.code, ", message:", err.message, "). Falling back to Cloud Function...");
    try {
      const fn = httpsCallable(functions, "addHeroImage");
      const res = await fn({
        imageUrl: data.imageUrl,
        order: numericOrder
      });
      if (res.data?.success) {
        return {
          id: res.data.id,
          _id: res.data.id,
          imageUrl: data.imageUrl,
          order: numericOrder,
          createdAt: res.data.createdAt,
          updatedAt: res.data.updatedAt
        };
      }
      // Cloud Function returned but without success flag — treat as unexpected response
      console.warn("Cloud Function addHeroImage returned unexpected response:", res.data);
      throw new Error("Cloud Function addHeroImage returned an unexpected response.");
    } catch (fnErr) {
      console.error("Cloud Function addHeroImage also failed (code:", fnErr.code, ", message:", fnErr.message, ", details:", fnErr.details, ")");
      throw fnErr;
    }
  }
};

/**
 * Updates an additional hero image document in Firestore.
 */
export const updateHeroImage = async (id, data) => {
  const numericOrder = data.order !== undefined
    ? (typeof data.order === "number" && !isNaN(data.order) ? data.order : (parseInt(data.order, 10) || 0))
    : undefined;

  try {
    const docRef = doc(db, "heroImages", id);
    const updatePayload = {
      updatedAt: serverTimestamp()
    };
    if (data.imageUrl !== undefined) {
      updatePayload.imageUrl = data.imageUrl;
    }
    if (numericOrder !== undefined) {
      updatePayload.order = numericOrder;
    }

    await updateDoc(docRef, updatePayload);
    const snapshot = await getDoc(docRef);
    return mapDoc(snapshot);
  } catch (err) {
    console.warn("Direct Firestore updateHeroImage failed (code:", err.code, ", message:", err.message, "). Falling back to Cloud Function...");
    try {
      const fn = httpsCallable(functions, "updateHeroImage");
      const res = await fn({
        id,
        imageUrl: data.imageUrl,
        order: numericOrder
      });
      if (res.data?.success) {
        return {
          id,
          _id: id,
          ...res.data
        };
      }
      console.warn("Cloud Function updateHeroImage returned unexpected response:", res.data);
      throw new Error("Cloud Function updateHeroImage returned an unexpected response.");
    } catch (fnErr) {
      console.error("Cloud Function updateHeroImage also failed (code:", fnErr.code, ", message:", fnErr.message, ", details:", fnErr.details, ")");
      throw fnErr;
    }
  }
};

/**
 * Deletes an additional hero image document from Firestore.
 */
export const deleteHeroImage = async (id) => {
  try {
    const docRef = doc(db, "heroImages", id);
    await deleteDoc(docRef);
    return { success: true };
  } catch (err) {
    console.warn("Direct Firestore deleteHeroImage failed (code:", err.code, ", message:", err.message, "). Falling back to Cloud Function...");
    try {
      const fn = httpsCallable(functions, "deleteHeroImage");
      const res = await fn({ id });
      return res.data;
    } catch (fnErr) {
      console.error("Cloud Function deleteHeroImage also failed (code:", fnErr.code, ", message:", fnErr.message, ", details:", fnErr.details, ")");
      throw fnErr;
    }
  }
};

